import React, { useState } from 'react';

const AboutPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  const sections = {
    overview: {
      title: "Electoral Grants Analytics Platform",
      content: "A powerful data visualization tool that transforms complex federal funding data into actionable insights. Our platform provides unprecedented transparency into how grants are distributed across Australia's electoral divisions."
    },
    data: {
      title: "Comprehensive Data Sources",
      content: "Our platform integrates grant data from the Australian Government's official grant databases with electoral boundary information from the Australian Electoral Commission. All data undergoes rigorous verification and normalization to ensure accuracy and reliability."
    },
    methodology: {
      title: "Robust Analysis Methodology",
      content: "Electorates are categorized and compared based on multiple factors including geographical characteristics, population demographics, and economic indicators. Our similarity algorithm uses weighted multi-factor analysis to identify genuinely comparable regions, enabling meaningful cross-electorate analysis."
    },
    technology: {
      title: "Modern Technical Implementation",
      content: "Built with React.js and modern web technologies, our platform leverages Chart.js for high-performance data visualization and Tailwind CSS for responsive design. This architecture allows for rapid data processing and seamless user interaction across all devices."
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto mt-20 px-4">
      <header className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-6 font-sora text-white border-b border-gray-700 pb-4">
          About <span className="text-blue-400">Electoral Grants</span>
        </h1>
        <p className="text-lg text-gray-300 font-inter leading-relaxed">
          Bringing transparency to government funding with powerful data visualization and analytics.
        </p>
      </header>
      
      {/* Section navigation */}
      <div className="flex flex-wrap gap-2 mb-8 sticky top-20 z-10 bg-gray-900/80 py-4 backdrop-blur-sm">
        {Object.keys(sections).map(key => (
          <button
            key={key}
            onClick={() => setActiveSection(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeSection === key 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-700/20' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {key.charAt(0).toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Main content */}
      <div className="space-y-12">
        <section className="bg-gray-800 p-8 rounded-lg shadow-xl border border-gray-700 transform transition-all duration-300 hover:shadow-blue-900/5">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 font-sora text-blue-400">
            {sections[activeSection].title}
          </h2>
          <p className="mb-6 font-inter text-gray-300 leading-relaxed text-lg">
            {sections[activeSection].content}
          </p>
          
          {activeSection === 'overview' && (
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="bg-gray-900 p-5 rounded-lg border border-gray-700">
                <div className="text-blue-500 mb-3 text-3xl">📊</div>
                <h3 className="font-bold text-lg mb-2 text-white">Interactive Visualizations</h3>
                <p className="text-gray-400 text-sm">Explore complex funding patterns through intuitive, interactive charts and graphs.</p>
              </div>
              <div className="bg-gray-900 p-5 rounded-lg border border-gray-700">
                <div className="text-blue-500 mb-3 text-3xl">🔍</div>
                <h3 className="font-bold text-lg mb-2 text-white">Comparative Analysis</h3>
                <p className="text-gray-400 text-sm">Compare funding across similar electorates to identify patterns and anomalies.</p>
              </div>
              <div className="bg-gray-900 p-5 rounded-lg border border-gray-700">
                <div className="text-blue-500 mb-3 text-3xl">📱</div>
                <h3 className="font-bold text-lg mb-2 text-white">Responsive Design</h3>
                <p className="text-gray-400 text-sm">Access insights on any device with our fully responsive, mobile-friendly interface.</p>
              </div>
            </div>
          )}
          
          {activeSection === 'data' && (
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <div className="text-green-400">✓</div>
                <p className="text-gray-300">Australian Government Grant Award Data (2015-2023)</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-green-400">✓</div>
                <p className="text-gray-300">Australian Electoral Commission Boundary Data</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-green-400">✓</div>
                <p className="text-gray-300">ABS Demographic and Census Information</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-green-400">✓</div>
                <p className="text-gray-300">Electoral Voting Pattern Analysis</p>
              </div>
            </div>
          )}
          
          {activeSection === 'methodology' && (
            <div className="mt-8 space-y-6">
              <div className="bg-gray-750 p-4 rounded-lg border border-gray-700">
                <h3 className="font-bold text-white mb-2">Electorate Similarity Algorithm</h3>
                <p className="text-gray-300 text-sm">Our proprietary algorithm considers geographical, demographic, and economic factors to identify truly comparable electorates.</p>
              </div>
              <div className="bg-gray-750 p-4 rounded-lg border border-gray-700">
                <h3 className="font-bold text-white mb-2">Pattern Recognition</h3>
                <p className="text-gray-300 text-sm">Advanced statistical methods identify funding patterns that may correlate with political, demographic, or temporal factors.</p>
              </div>
              <div className="bg-gray-750 p-4 rounded-lg border border-gray-700">
                <h3 className="font-bold text-white mb-2">Data Normalization</h3>
                <p className="text-gray-300 text-sm">Population-adjusted metrics ensure fair comparisons between electorates of varying sizes and densities.</p>
              </div>
            </div>
          )}
          
          {activeSection === 'technology' && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 p-3 rounded-lg text-center">
                <div className="text-2xl mb-2">⚛️</div>
                <div className="text-white font-bold">React.js</div>
              </div>
              <div className="bg-gray-900 p-3 rounded-lg text-center">
                <div className="text-2xl mb-2">📊</div>
                <div className="text-white font-bold">Chart.js</div>
              </div>
              <div className="bg-gray-900 p-3 rounded-lg text-center">
                <div className="text-2xl mb-2">🎨</div>
                <div className="text-white font-bold">Tailwind CSS</div>
              </div>
              <div className="bg-gray-900 p-3 rounded-lg text-center">
                <div className="text-2xl mb-2">🔄</div>
                <div className="text-white font-bold">Modern JS</div>
              </div>
            </div>
          )}
        </section>
        
        <section className="bg-gray-800 p-8 rounded-lg shadow-xl border border-gray-700">
          <h2 className="text-2xl font-bold mb-6 font-sora text-white">Get in Touch</h2>
          <p className="font-inter text-gray-300 leading-relaxed mb-8">
            Have questions about the Electoral Grants platform or interested in accessing our API for your own analysis? Our team is ready to assist.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:contact@electoral-grants.com" 
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-center shadow-lg shadow-blue-900/20"
            >
              Contact Us
            </a>
            <a 
              href="#"
              className="px-6 py-3 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors text-center"
            >
              Documentation
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
